// Timezone-aware time conversion — the ONE place wall-clock ↔ epoch conversion
// happens, so a user-chosen IANA timezone is applied consistently everywhere
// (scheduling interpretation + every displayed time). Before this, the whole app
// implicitly used the browser's local zone; every conversion now routes through
// here with an explicit `tz`, defaulting to the browser zone so nothing changes
// for a user who never picks a different one.
//
// No dependencies: all zone math is done with Intl.DateTimeFormat, which carries
// the IANA/DST database in the runtime. A `datetime-local` value is always the
// bare wall-clock string "YYYY-MM-DDTHH:mm" (no zone), which is exactly why we
// must say WHICH zone it means when turning it into an absolute instant.

/** The browser's own IANA zone, e.g. "America/New_York". The default when the
 * user hasn't chosen one. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** True if `tz` is an IANA zone this runtime accepts — used to validate a stored
 * or chosen value before trusting it (a bad zone would throw in formatToParts). */
export function isValidTimezone(tz: string): boolean {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

// The wall-clock fields of an instant, as seen in a given zone.
interface Parts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function partsInTz(epochMs: number, tz: string): Parts {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const map = new Map<string, string>();
  for (const p of dtf.formatToParts(new Date(epochMs))) {
    if (p.type !== "literal") map.set(p.type, p.value);
  }
  const n = (k: string): number => Number(map.get(k));
  return {
    year: n("year"),
    month: n("month"),
    day: n("day"),
    hour: n("hour"),
    minute: n("minute"),
    second: n("second"),
  };
}

/** Zone offset (ms to ADD to UTC to get local wall-clock) for `tz` at `epochMs`.
 * e.g. New York in winter → -5h. Derived by asking Intl what the wall clock is
 * there and differencing against the same fields read as UTC. */
function offsetMs(epochMs: number, tz: string): number {
  const p = partsInTz(epochMs, tz);
  const asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUTC - epochMs;
}

/**
 * Interpret a `datetime-local` wall-clock string ("YYYY-MM-DDTHH:mm") as a time
 * in `tz` and return the absolute epoch (ms). Two-pass to stay correct across a
 * DST transition: the first offset guess is refined once if the resulting
 * instant lands in a different offset (spring-forward / fall-back edge).
 */
export function wallClockToEpoch(local: string, tz: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) return NaN;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const h = Number(m[4]);
  const mi = Number(m[5]);
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const off1 = offsetMs(guess, tz);
  let epoch = guess - off1;
  const off2 = offsetMs(epoch, tz);
  if (off2 !== off1) epoch = guess - off2;
  return epoch;
}

/** Epoch (ms) → `datetime-local` value ("YYYY-MM-DDTHH:mm") as it reads in `tz`.
 * The inverse of {@link wallClockToEpoch}; feeds the schedule `<input>`. */
export function epochToInputValue(epochMs: number, tz: string): string {
  const p = partsInTz(epochMs, tz);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/** Stable "YYYY-MM-DD" day key for an instant AS SEEN in `tz` — so the calendar
 * buckets a post onto the day it falls on in the chosen zone, not the browser's. */
export function dayKeyInTz(epochMs: number, tz: string): string {
  const p = partsInTz(epochMs, tz);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/** Format an instant for display in `tz` (thin wrapper over Intl so callers pass
 * their usual options but the zone is always explicit). */
export function formatInTz(
  epochMs: number,
  tz: string,
  opts: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat(undefined, { timeZone: tz, ...opts }).format(new Date(epochMs));
}

/** A short, human label for a zone, e.g. "America/New_York (EDT)". Falls back to
 * the raw id if the short name can't be resolved. */
export function timezoneLabel(tz: string, epochMs = Date.now()): string {
  try {
    const abbr = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    })
      .formatToParts(new Date(epochMs))
      .find((p) => p.type === "timeZoneName")?.value;
    return abbr ? `${tz} (${abbr})` : tz;
  } catch {
    return tz;
  }
}

/** The IANA zones this runtime knows, for the picker. Falls back to a curated
 * common set if `Intl.supportedValuesOf` is unavailable (older engines). */
export function supportedTimezones(): string[] {
  try {
    const fn = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] })
      .supportedValuesOf;
    if (fn) {
      const zones = fn("timeZone");
      // supportedValuesOf omits the "UTC" alias — ensure it's always offerable.
      return zones.includes("UTC") ? zones : ["UTC", ...zones];
    }
  } catch {
    /* fall through */
  }
  return COMMON_TIMEZONES;
}

/** Curated fallback list (also handy for grouping the picker's top options). */
export const COMMON_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Athens",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
  "UTC",
];
